import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { runBiddingPreloadIfNeeded, canRunBiddingPreload } from '@/utils/biddingPreload'
import { lazyLayoutView } from '@/utils/lazyView'
import { ensureSmartHubInMenuList } from '@/utils/smartCreateHubMenu'
import { isQaChannelIncomeMenuEnabled } from '@/utils/qaChannelIncomeFeature'
import { isPermissionsReady, isSuperAdmin } from '@/utils/userPermission'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { title: '登录', public: true }
  },
  {
    path: '/auth/feishu-callback',
    name: 'FeishuCallback',
    component: () => import('@/views/FeishuCallback.vue'),
    meta: { title: '登录回调', public: true }
  },
  {
    path: '/huawei-callback-debug',
    name: 'HuaweiCallbackDebug',
    component: () => import('@/views/HuaweiCallbackDebug.vue'),
    meta: { title: '鲸鸿转化回传联调', public: true }
  },
  {
    path: '/',
    component: () => import('@/components/Layout/index.vue'),
    redirect: '/bidding-delivery',
    children: [
      {
        path: 'product',
        name: 'Product',
        component: lazyLayoutView(() => import('@/views/Product.vue')),
        meta: { title: '产品管理', menuCode: 'product' }
      },
      {
        path: 'subject-token',
        name: 'SubjectToken',
        component: lazyLayoutView(() => import('@/views/SubjectToken.vue')),
        meta: { title: '主体token管理', menuCode: 'subject-token' }
      },
      {
        path: 'oauth-config',
        name: 'OAuthConfig',
        component: lazyLayoutView(() => import('@/views/OAuthConfig.vue')),
        meta: { title: 'OAuth配置', menuCode: 'oauth-config' }
      },
      {
        path: 'dict-item',
        name: 'DictItem',
        component: lazyLayoutView(() => import('@/views/DictItem.vue')),
        meta: { title: '字典管理', menuCode: 'dict-item' }
      },
      {
        path: 'media-trantype',
        name: 'MediaTranType',
        component: lazyLayoutView(() => import('@/views/MediaTranType.vue')),
        meta: { title: '转化类型维护', menuCode: 'media-trantype' }
      },
      {
        path: 'sys-user-permission',
        name: 'SysUserPermission',
        component: lazyLayoutView(() => import('@/views/SysUserPermission.vue')),
        meta: { title: '账号权限', menuCode: 'sys-user-permission' }
      },
      {
        path: 'ddl-exec',
        name: 'DdlExec',
        component: lazyLayoutView(() => import('@/views/DdlExec.vue')),
        meta: { title: 'DDL 执行', menuCode: 'ddl-exec' }
      },
      {
        path: 'platform-accounts',
        name: 'PlatformAccounts',
        component: lazyLayoutView(() => import('@/views/PlatformAccounts.vue')),
        meta: { title: '账户列表', menuCode: 'platform-accounts' }
      },
      {
        path: 'jl-media-report-ops',
        name: 'JlMediaReportOps',
        component: lazyLayoutView(() => import('@/views/JlMediaReportOps.vue')),
        meta: { title: '巨量报表联调', menuCode: 'jl-media-report-ops' }
      },
      {
        path: 'platform-api-tools',
        name: 'PlatformApiTools',
        component: lazyLayoutView(() => import('@/views/PlatformApiTools.vue')),
        meta: { title: '接口调试工具', menuCode: 'platform-api-tools' }
      },
      {
        path: 'qa-channel-income',
        name: 'QaChannelIncomeSummary',
        component: lazyLayoutView(() => import('@/views/QaChannelIncomeSummary.vue')),
        meta: { title: '分渠道收入汇总', menuCode: 'qa-channel-income' }
      },
      {
        path: 'qa-alliance-income',
        name: 'QaAllianceIncomeSummary',
        component: lazyLayoutView(() => import('@/views/QaAllianceIncomeSummary.vue')),
        meta: { title: '联盟收入汇总', menuCode: 'qa-alliance-income' }
      },
      {
        path: 'qa-channel-income-account-conf',
        name: 'QaChannelIncomeAccountConf',
        component: lazyLayoutView(() => import('@/views/QaChannelIncomeAccountConf.vue')),
        meta: { title: '广告联盟收入账号配置', menuCode: 'qa-channel-income-account-conf' }
      },
      {
        path: 'global-funnel',
        redirect: to => ({ path: '/global-funnel-new', query: to.query })
      },
      {
        path: 'global-funnel-new',
        name: 'GlobalFunnelNew',
        component: lazyLayoutView(() => import('@/views/GlobalFunnelNew.vue')),
        meta: { title: '全局漏斗（新）', menuCode: 'global-funnel-new', keepAlive: true }
      },
      {
        path: 'roi-report',
        name: 'RoiReport',
        component: lazyLayoutView(() => import('@/views/RoiReport.vue')),
        meta: { title: 'ROI报表', menuCode: 'roi-report' }
      },
      {
        path: 'roi-trend',
        name: 'RoiTrend',
        component: lazyLayoutView(() => import('@/views/RoiTrend.vue')),
        meta: { title: '趋势图', menuCode: 'roi-trend' }
      },
      {
        path: 'ad-retention',
        name: 'AdRetentionAnalysis',
        component: lazyLayoutView(() => import('@/views/AdRetentionAnalysis.vue')),
        meta: { title: '用户留存分析', menuCode: 'ad-retention' }
      },
      {
        path: 'new-user-retention',
        name: 'NewUserRetentionAnalysis',
        component: lazyLayoutView(() => import('@/views/NewUserRetentionAnalysis.vue')),
        meta: { title: '新用户留存', menuCode: 'new-user-retention' }
      },
      {
        path: 'account-report-schedule',
        name: 'AccountReportSchedule',
        component: lazyLayoutView(() => import('@/views/AccountReportScheduleHub.vue')),
        meta: {
          title: '报表与定时',
          menuCode: 'account-report-schedule',
          menuCodes: ['account-report-schedule', 'account-report-hour-schedule', 'schedule-job-log']
        }
      },
      {
        path: 'account-report-hour-schedule',
        redirect: '/account-report-schedule?tab=hour'
      },
      {
        path: 'schedule-job-log',
        redirect: to => ({
          path: '/account-report-schedule',
          query: { ...to.query, tab: 'jobs' }
        })
      },
      {
        path: 'smart-create-hub',
        name: 'SmartCreateHub',
        component: lazyLayoutView(() => import('@/views/SmartCreateHub.vue')),
        meta: { title: '智投创编', menuCode: 'smart-create-hub', keepAlive: true }
      },
      {
        path: 'smart-create-hub/tour',
        name: 'SmartCreateHubTour',
        meta: { title: '智投创编分步引导', menuCode: 'smart-create-hub' },
        redirect: { name: 'SmartCreateHub', query: { tour: '1' } }
      },
      {
        path: 'feishu-notify',
        name: 'FeishuNotify',
        component: lazyLayoutView(() => import('@/views/FeishuNotify.vue')),
        meta: { title: '飞书预警', menuCode: 'feishu-notify' }
      },
      {
        path: 'alert-metric-config',
        name: 'AlertMetricConfig',
        component: lazyLayoutView(() => import('@/views/AlertMetricConfig.vue')),
        meta: { title: '预警类型配置', menuCode: 'alert-metric-config' }
      },
      {
        path: 'funnel-hour-yoy-alert',
        name: 'FunnelHourYoyAlert',
        component: lazyLayoutView(() => import('@/views/FunnelHourYoyAlert.vue')),
        meta: { title: '漏斗小时同比', menuCode: 'funnel-hour-yoy-alert' }
      },
      {
        path: 'callback-center',
        redirect: '/callback-center/callback-hub'
      },
      {
        path: 'callback-center/callback-hub',
        name: 'CallbackHub',
        component: lazyLayoutView(() => import('@/views/CallbackHub.vue')),
        meta: {
          title: '回传策略与账户',
          menuCodes: ['callback-config', 'back-config-account'],
          keepAlive: true
        }
      },
      {
        path: 'callback-center/back-config-account',
        redirect: '/callback-center/callback-hub'
      },
      {
        path: 'callback-center/back-config-threshold',
        name: 'BackConfigThreshold',
        component: lazyLayoutView(() => import('@/views/BackConfigThreshold.vue')),
        meta: { title: '回传阈值配置', menuCode: 'back-config-threshold' }
      },
      {
        path: 'callback-center/callback-config',
        redirect: '/callback-center/callback-hub'
      },
      {
        path: 'callback-center/callback-report',
        name: 'CallbackReport',
        component: lazyLayoutView(() => import('@/views/CallbackReport.vue')),
        meta: { title: '回传报表', menuCode: 'callback-report' }
      },
      {
        path: 'callback-center/callback-back-log',
        name: 'CallbackBackLog',
        component: lazyLayoutView(() => import('@/views/CallbackBackLog.vue')),
        meta: { title: '回传日志', menuCode: 'callback-back-log' }
      },
      {
        path: 'callback-center/back-cal-log',
        name: 'BackCalLog',
        component: lazyLayoutView(() => import('@/views/BackCalLog.vue')),
        meta: { title: '回传计算日志', menuCode: 'back-cal-log' }
      },
      {
        path: 'callback-center/manual-convert-upload',
        name: 'ManualConvertUpload',
        component: lazyLayoutView(() => import('@/views/ManualConvertUpload.vue')),
        meta: { title: '批量转化手动回传', menuCode: 'manual-convert-upload' }
      },
      {
        path: 'material-local-pool',
        name: 'MaterialLocalPool',
        component: lazyLayoutView(() => import('@/views/MaterialLocalPool.vue')),
        meta: { title: '本地素材池', menuCode: 'material-local-pool' }
      },
      {
        path: 'jh-pps-content',
        name: 'JhPpsContent',
        component: lazyLayoutView(() => import('@/views/JhPpsContent.vue')),
        meta: { title: '鲸鸿内容导入', menuCode: 'jh-pps-content' }
      },
      {
        path: 'material',
        name: 'Material',
        component: lazyLayoutView(() => import('@/views/Material.vue')),
        meta: { title: '素材中心', menuCode: 'material' }
      },
      {
        path: 'material-acl',
        name: 'MaterialAclShare',
        component: lazyLayoutView(() => import('@/views/MaterialAclShare.vue')),
        meta: { title: '素材共享', menuCode: 'material' }
      },
      {
        path: 'product-main-image',
        name: 'ProductMainImage',
        component: lazyLayoutView(() => import('@/views/ProductMainImage.vue')),
        meta: { title: '产品主图', menuCode: 'product-main-image' }
      },
      {
        path: 'material-derive-workbench',
        name: 'MaterialDeriveWorkbench',
        component: lazyLayoutView(() => import('@/views/MaterialDeriveWorkbench.vue')),
        meta: { title: '素材加工台', menuCode: 'material-derive-workbench' }
      },
      {
        path: 'material-spec',
        name: 'MaterialSpec',
        component: lazyLayoutView(() => import('@/views/MaterialSpec.vue')),
        meta: { title: '素材尺寸配置', menuCode: 'material-spec' }
      },
      {
        path: 'material-weight',
        name: 'MaterialWeight',
        component: lazyLayoutView(() => import('@/views/MaterialWeight.vue')),
        meta: { title: '素材权重', menuCode: 'material-weight' }
      },
      {
        path: 'smart-create',
        redirect: '/auto-create-rule'
      },
      {
        path: 'targeting-config',
        name: 'TargetingConfig',
        component: lazyLayoutView(() => import('@/views/TargetingConfig.vue')),
        meta: { title: '定向配置', menuCode: 'targeting-config' }
      },
      {
        path: 'targeting-package',
        name: 'TargetingPackage',
        component: lazyLayoutView(() => import('@/views/TargetingPackage.vue')),
        meta: { title: '人群定向包', menuCode: 'targeting-package' }
      },
      {
        path: 'dmp-audience',
        name: 'DmpAudience',
        component: lazyLayoutView(() => import('@/views/DmpAudience.vue')),
        meta: { title: 'DMP人群包', menuCode: 'dmp-audience' }
      },
      {
        path: 'hap-link',
        name: 'HapLink',
        component: lazyLayoutView(() => import('@/views/HapLink.vue')),
        meta: { title: '链接管理', menuCode: 'hap-link' }
      },
      {
        path: 'product-creative-config',
        name: 'ProductCreativeConfig',
        component: lazyLayoutView(() => import('@/views/ProductCreativeConfig.vue')),
        meta: { title: '创意配置', menuCode: 'product-creative-config' }
      },
      {
        path: 'config-mark-industry-labels',
        name: 'ConfigMarkIndustryLabels',
        component: lazyLayoutView(() => import('@/views/ConfigMarkIndustryLabels.vue')),
        meta: { title: '行业标签', menuCode: 'product-creative-config' }
      },
      {
        path: 'auto-create-rule',
        name: 'AutoCreateRule',
        component: lazyLayoutView(() => import('@/views/AutoCreateRule.vue')),
        meta: { title: '创建规则', menuCode: 'auto-create-rule' }
      },
      {
        path: 'create-intent',
        name: 'CreateIntent',
        component: lazyLayoutView(() => import('@/views/CreateIntent.vue')),
        meta: { title: '一页纸创编', menuCode: 'create-intent' }
      },
      {
        path: 'auto-delivery-center',
        name: 'AutoDeliveryCenter',
        component: lazyLayoutView(() => import('@/views/AutoDeliveryCenter.vue')),
        meta: { title: '执行日志', menuCode: 'auto-delivery-center' }
      },
      {
        path: 'auto-create-schedule',
        name: 'AutoCreateSchedule',
        component: lazyLayoutView(() => import('@/views/AutoCreateSchedule.vue')),
        meta: { title: '定时执行', menuCode: 'auto-create-schedule' }
      },
      {
        path: 'bidding-delivery',
        name: 'BiddingDelivery',
        component: lazyLayoutView(() => import('@/views/BiddingDelivery.vue')),
        meta: { title: '竞价投放', menuCode: 'bidding-delivery', keepAlive: true }
      },
      {
        path: 'jh-ocpx-compensation',
        name: 'JhOcpxCompensationMetric',
        component: lazyLayoutView(() => import('@/views/JhOcpxCompensationMetric.vue')),
        meta: { title: '鲸鸿赔付', menuCode: 'jh-ocpx-compensation' }
      },
      {
        path: 'compensation-report',
        name: 'CompensationReport',
        component: lazyLayoutView(() => import('@/views/CompensationReport.vue')),
        meta: { title: '赔付表', menuCode: 'compensation-report' }
      },
      {
        path: 'bidding-ocpx-compensation',
        redirect: { path: '/compensation-report', query: { platform: 'jh' } }
      },
      {
        path: 'bidding-policy',
        name: 'BiddingPolicy',
        component: lazyLayoutView(() => import('@/views/BiddingPolicy.vue')),
        meta: { title: '投放策略', menuCode: 'bidding-policy' }
      },
      {
        path: 'delivery-config',
        name: 'DeliveryConfig',
        component: lazyLayoutView(() => import('@/views/DeliveryConfig.vue')),
        meta: { title: '投放配置', menuCode: 'delivery-config', keepAlive: true }
      },
      {
        path: 'task-emptyspace-record',
        name: 'TaskEmptyspaceRecord',
        component: lazyLayoutView(() => import('@/views/TaskEmptyspaceRecord.vue')),
        meta: { title: '拉空记录', menuCode: 'task-emptyspace-record' }
      },
      {
        path: 'task-operate-log',
        name: 'TaskOperateLog',
        component: lazyLayoutView(() => import('@/views/TaskOperateLog.vue')),
        meta: { title: '任务操作日志', menuCode: 'task-operate-log' }
      },
      {
        path: 'guide/manual',
        name: 'GuideManual',
        component: lazyLayoutView(() => import('@/views/GuideManual.vue')),
        meta: { title: '使用手册', menuCode: 'guide/manual' }
      },
      {
        path: 'guide/todo',
        name: 'GuideUpdateLog',
        component: lazyLayoutView(() => import('@/views/GuideUpdateLog.vue')),
        meta: { title: '更新记录', menuCode: 'guide/todo' }
      },
      {
        path: 'sys-operation-log',
        name: 'SysOperationLog',
        component: lazyLayoutView(() => import('@/views/SysOperationLog.vue')),
        meta: { title: '系统操作日志', menuCode: 'sys-operation-log' }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// 菜单编码与 path 对应：无权限时按序跳转到第一个有权限的页面；竞价投放为首屏默认，故排在最前
const MENU_CODE_ORDER = [
  'bidding-delivery',
  'product', 'subject-token', 'oauth-config', 'dict-item', 'media-trantype', 'sys-user-permission', 'ddl-exec', 'platform-accounts', 'jl-media-report-ops', 'platform-api-tools', 'qa-channel-income', 'qa-alliance-income', 'qa-channel-income-account-conf', 'global-funnel-new', 'roi-report', 'roi-trend', 'ad-retention', 'new-user-retention', 'account-report-schedule', 'account-report-hour-schedule', 'schedule-job-log', 'feishu-notify', 'alert-metric-config', 'funnel-hour-yoy-alert',
  'callback-center', 'back-config-account', 'back-config-threshold', 'callback-config', 'callback-report', 'callback-back-log', 'back-cal-log', 'manual-convert-upload',
  'material-local-pool', 'jh-pps-content', 'material', 'material-spec', 'material-weight', 'product-main-image', 'material-derive-workbench', 'smart-create-hub', 'targeting-config', 'targeting-package', 'dmp-audience', 'hap-link', 'product-creative-config',
  'create-intent', 'auto-create-rule', 'auto-delivery-center', 'auto-create-schedule', 'jh-ocpx-compensation', 'compensation-report', 'bidding-policy', 'delivery-config', 'task-emptyspace-record', 'task-operate-log',
  'guide/manual', 'guide/todo', 'sys-operation-log'
]

const LAYOUT_CHILDREN = (routes.find(r => r.path === '/') || {}).children || []

/** 仅认叶子菜单码；父分组码不授权（与 Layout.canShowMenu 一致） */
function hasMenuAccess(allowedMenus, code) {
  if (!code || !Array.isArray(allowedMenus)) return false
  return allowedMenus.indexOf(code) !== -1
}

function routePathOf(child) {
  if (!child || !child.path) return ''
  return child.path.startsWith('/') ? child.path : '/' + child.path
}

function routeRequiredMenuCodes(child) {
  const meta = (child && child.meta) || {}
  if (Array.isArray(meta.menuCodes) && meta.menuCodes.length) return meta.menuCodes
  if (meta.menuCode) return [meta.menuCode]
  return []
}

function canAccessRouteByMenus(child, allowedMenus) {
  const codes = routeRequiredMenuCodes(child)
  if (!codes.length) return false
  return codes.some(c => hasMenuAccess(allowedMenus, c))
}

/** 按路由 meta.menuCode(s) 解析首个可访问 path，避免父菜单码（如 callback-center）与页面码不一致导致死循环 */
function findFirstAccessiblePath(allowedMenus) {
  const menus = ensureSmartHubInMenuList(Array.isArray(allowedMenus) ? allowedMenus : [])
  if (!menus.length) return '/guide/todo'

  const codeToChild = new Map()
  for (const child of LAYOUT_CHILDREN) {
    if (!child.component) continue
    for (const code of routeRequiredMenuCodes(child)) {
      if (!codeToChild.has(code)) codeToChild.set(code, child)
    }
  }

  for (const code of MENU_CODE_ORDER) {
    if (menus.indexOf(code) === -1) continue
    const child = codeToChild.get(code)
    if (child && canAccessRouteByMenus(child, menus)) return routePathOf(child)
  }

  for (const child of LAYOUT_CHILDREN) {
    if (!child.component) continue
    if (canAccessRouteByMenus(child, menus)) return routePathOf(child)
  }
  return '/guide/todo'
}

function redirectToFirstAccessible(allowedMenus, to, next) {
  const target = findFirstAccessiblePath(allowedMenus)
  if (target && target !== to.path) {
    next(target)
  } else {
    next()
  }
}

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('token')
  const isPublic = to.matched.some(r => r.meta && r.meta.public)
  if (!token && !isPublic) {
    next('/login')
    return
  }
  // 分渠道收入汇总：与 auto-create.qa-quickapp-link.enabled 联动，关闭时禁止进入（不受 menus 权限影响）
  if (token && !isPublic && to.path.includes('/qa-channel-income') && !isQaChannelIncomeMenuEnabled()) {
    let allowedMenus = []
    try {
      const raw = localStorage.getItem('allowedMenus')
      if (raw) allowedMenus = JSON.parse(raw)
    } catch (e) { /* ignore */ }
    allowedMenus = ensureSmartHubInMenuList(Array.isArray(allowedMenus) ? allowedMenus : [])
    redirectToFirstAccessible(allowedMenus, to, next)
    return
  }
  // 已登录且当前路由需要菜单权限校验
  const menuCodes = to.meta && Array.isArray(to.meta.menuCodes) ? to.meta.menuCodes : null
  const menuCode = to.meta && to.meta.menuCode
  const codesToCheck = menuCodes && menuCodes.length ? menuCodes : (menuCode ? [menuCode] : [])
  if (token && !isPublic && codesToCheck.length > 0) {
    try {
      if (isSuperAdmin()) {
        next()
        if (token && !isPublic && canRunBiddingPreload()) runBiddingPreloadIfNeeded(store)
        return
      }
    } catch (e) { /* ignore */ }
    if (!isPermissionsReady()) {
      // 权限未就绪时不放行需菜单校验的路由，避免短暂越权
      redirectToFirstAccessible([], to, next)
      return
    }
    let allowedMenus = []
    try {
      const raw = localStorage.getItem('allowedMenus')
      if (raw) allowedMenus = JSON.parse(raw)
    } catch (e) { /* ignore parse errors */ }
    const prevSnap = JSON.stringify(allowedMenus)
    allowedMenus = ensureSmartHubInMenuList(Array.isArray(allowedMenus) ? allowedMenus : [])
    if (JSON.stringify(allowedMenus) !== prevSnap) {
      try {
        localStorage.setItem('allowedMenus', JSON.stringify(allowedMenus))
      } catch (e) { /* ignore */ }
    }
    const allowed = codesToCheck.some(c => hasMenuAccess(allowedMenus, c))
    if (!allowed) {
      redirectToFirstAccessible(allowedMenus, to, next)
      return
    }
  }
  next()
  // 每日首次进入系统时在后台预加载竞价投放数据，进入该页时首屏更快
  if (token && !isPublic && canRunBiddingPreload()) runBiddingPreloadIfNeeded(store)
})

// 避免 el-menu router 模式在守卫重定向时向控制台抛 NavigationFailure（redirected / duplicated）
const rawPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return rawPush.call(this, location).catch(err => {
    if (VueRouter.isNavigationFailure && VueRouter.isNavigationFailure(err)) {
      return err
    }
    return Promise.reject(err)
  })
}
const rawReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return rawReplace.call(this, location).catch(err => {
    if (VueRouter.isNavigationFailure && VueRouter.isNavigationFailure(err)) {
      return err
    }
    return Promise.reject(err)
  })
}

/** 开发环境 HMR / 发版后旧 chunk 名失效时自动刷新一次，避免 ChunkLoadError 红屏 */
router.onError((error) => {
  const msg = error && error.message ? String(error.message) : ''
  const isChunkFail = (error && error.name === 'ChunkLoadError')
    || /Loading chunk [\s\S]+ failed/i.test(msg)
    || /ChunkLoadError/i.test(msg)
  if (!isChunkFail) {
    throw error
  }
  const reloadKey = 'automark_chunk_reload_ts'
  const last = Number(sessionStorage.getItem(reloadKey) || 0)
  const now = Date.now()
  if (!last || now - last > 8000) {
    sessionStorage.setItem(reloadKey, String(now))
    window.location.reload()
    return
  }
  throw error
})

export default router
